import * as React from 'react';
import styled, { css } from 'styled-components';

import { getMessagesList } from '../../api/messages';
import RichMessageText from '../../molecules/RichMessageText';
import Loader from '../../atoms/Loader';
import { formatApiDateTime } from '../../utils/parseApiDateTime';

type DailyMessageProps = {
  hideHeader?: boolean;
  fillParent?: boolean;
  /** Bump to force the list to re-read (e.g. after sending). */
  manuallyUpdate?: number;
};

/**
 * Employee + Inspector dashboard block: today's messages, body only.
 *
 * Deliberately not a Table - the sender/addresses columns crowded out the one
 * thing the reader is here for.
 *
 * No client-side filtering either. The list rules in MessageViewSet already
 * scope to the caller's branch, their role, whether the message is addressed to
 * them, and whether it is showing today. The old copy re-did the last two here
 * by string-matching `addressesList` against the user's name and comparing
 * `publicationDate` to today - the latter hid holiday notices, whose whole
 * point is to show on days other than the one they were written.
 */
const DailyMessage = ({
  hideHeader = false,
  fillParent = false,
  manuallyUpdate,
}: DailyMessageProps = {}) => {
  const [items, setItems] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    getMessagesList({ limit: 25, offset: 0 })
      .then(response => {
        if (cancelled) return;
        setItems(
          (response.data?.results || []).filter(
            row => row && row.text,
          ),
        );
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [manuallyUpdate]);

  return (
    <Wrapper $fillParent={fillParent}>
      {!hideHeader && <Header>Message of the day</Header>}
      <Content $fillParent={fillParent}>
        {isLoading && (
          <Centered>
            <Loader />
          </Centered>
        )}

        {!isLoading && !items.length && (
          <Empty>No messages today.</Empty>
        )}

        {!isLoading &&
          items.map(item => (
            <Item key={item.id}>
              <Meta>
                {/* `sender` is a SQL Concat of first+last name, so it can come
                    back as a lone space on a user with neither. */}
                {(item.sender || '').trim() && (
                  <Sender>{item.sender.trim()}</Sender>
                )}
                {formatApiDateTime(item.publicationDate) && (
                  <Sent>
                    {formatApiDateTime(item.publicationDate)}
                  </Sent>
                )}
              </Meta>
              <RichMessageText html={item.text} />
            </Item>
          ))}
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $fillParent?: boolean }>`
  padding: ${({ $fillParent }) => ($fillParent ? '0' : '6px')};
  width: ${({ $fillParent }) => ($fillParent ? '100%' : '270px')};
  height: ${({ $fillParent }) => ($fillParent ? 'auto' : '340px')};
  border: ${({ $fillParent }) =>
    $fillParent ? 'none' : '1px solid var(--border)'};
  border-radius: ${({ $fillParent }) => ($fillParent ? '0' : '5px')};
  box-sizing: border-box;
  overflow: hidden;
  @media only screen and (max-width: 840px) {
    display: none;
    width: 100%;
  }
`;

const Header = styled.div`
  background: var(--primary);
  color: var(--defaultBackground);
  text-align: center;
  border-radius: 5px;
  padding: 8px 0;
  margin-bottom: 10px;
`;

const Content = styled.div<{ $fillParent?: boolean }>`
  height: ${({ $fillParent }) => ($fillParent ? 'auto' : '280px')};
  /* Cap the list so a busy day can't stretch the dashboard row. The three
     dashboard cards sit in an align-items: stretch grid, so an unbounded
     list here leaves the neighbouring columns blank. */
  max-height: ${({ $fillParent }) => ($fillParent ? '300px' : 'none')};
  overflow-y: auto;
  /* Browsers promote overflow-x: visible to auto once overflow-y is set,
     which would add a phantom horizontal scrollbar. */
  overflow-x: hidden;
  padding: 0 6px;
  box-sizing: border-box;
`;

const Item = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--mainText);
  text-align: left;

  & + & {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border);
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  margin-bottom: 4px;
`;

/* One type treatment for the whole byline - the name and the timestamp read
   as a single line, so they share size, weight and colour. Change it here and
   both follow. */
const metaText = css`
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--neutral-500);
`;

const Sender = styled.span`
  ${metaText}
`;

const Sent = styled.span`
  ${metaText}
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const Empty = styled.div`
  text-align: center;
  padding: 24px 0;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

export default DailyMessage;
